/**
 * 汎用の通知ダイアログ (OK ボタンのみ)。
 *
 * - 保存 / 削除などの「完了しました」を、画面に埋め込まずポップアップで知らせる用途。
 * - 見た目は `ConfirmDialog` に揃える (既存の `.modal-backdrop` / `.modal` 系 CSS を流用し、
 *   `window.alert` のようなホスト名ヘッダーが付かないアプリ独自モーダルにする)。
 * - 背景クリック・ESC キーでは閉じない。OK ボタンのみで閉じる (見落とし防止)。
 *   OK ボタンにフォーカスを当てるため、Enter キーでも閉じられる。
 *
 * 改行は `\n` で受け取り、`<br />` に変換して複数行表示する。
 */
import { Fragment } from 'react';

export interface NoticeDialogProps {
  /** 表示状態。false のときは何も描画しない。 */
  open: boolean;
  /** ヘッダーに表示するタイトル。省略時は「完了」。 */
  title?: string;
  /** 本文。`\n` で改行。 */
  message: string;
  /** 閉じるボタンのラベル。省略時は「OK」。 */
  closeLabel?: string;
  /** 閉じるときのコールバック */
  onClose: () => void;
}

export function NoticeDialog(props: NoticeDialogProps): React.JSX.Element | null {
  const { open, title = '完了', message, closeLabel = 'OK', onClose } = props;

  if (!open) return null;

  // 改行 (\n) を <br /> に変換して複数行表示
  const lines = message.split('\n');

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="notice-dialog"
      // 背景クリックでは閉じない (onClick 未設定)。ConfirmDialog と同じ位置 (上寄せ) に表示する。
      style={{ zIndex: 1300, alignItems: 'flex-start', paddingTop: '5vh' }}
    >
      <div className="modal modal--single-column" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2 style={{ margin: 0, fontSize: 15 }}>{title}</h2>
        </div>
        <div className="modal-body modal-body--single-column">
          <div className="form-pane">
            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }} data-testid="notice-dialog-message">
              {lines.map((line, idx) => (
                <Fragment key={idx}>
                  {line}
                  {idx < lines.length - 1 ? <br /> : null}
                </Fragment>
              ))}
            </p>
          </div>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            data-testid="notice-dialog-close-button"
            className="primary"
            onClick={onClose}
            autoFocus
          >
            {closeLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
